import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.5 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskLine = ({ children, i, className }) => (
  <span className="block overflow-hidden">
    <motion.span
      custom={i}
      variants={lineVariants}
      initial="hidden"
      animate="visible"
      className={`block ${className || ""}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ section }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  if (!section) return null;

  const followCta = () => {
    const href = section.button_url;
    if (!href) return;

    if (href.startsWith("#")) {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    window.location.href = href;
  };

  return (
    <section ref={ref} data-testid="hero" className="relative h-screen w-full overflow-hidden bg-black">
      <motion.div style={{ y: imgY, scale }} className="absolute inset-0 will-change-transform">
        <img
          src={section.image}
          alt={section.metadata?.image_alt || section.title || "Susegado Strike"}
          className="w-full h-full object-cover grayscale-[30%]"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: textY, opacity: fade }}
        className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-28"
      >
        {section.eyebrow && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="flex items-center gap-4 mb-6"
          >
            <span className="h-px w-12 bg-[#E85A24]" />
            <span className="font-oswald uppercase tracking-[0.35em] text-xs md:text-sm text-[#E85A24]">
              {section.eyebrow}
            </span>
          </motion.div>
        )}

        <h1 className="font-anton uppercase leading-[0.82] text-[#FAFAFA] text-[19vw] md:text-[15vw] lg:text-[13vw]">
          <MaskLine i={0}>{section.title}</MaskLine>
          <MaskLine i={1} className="text-[#E85A24]">{section.title_accent}</MaskLine>
        </h1>

        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          {section.body && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              className="font-dm text-[#A3A3A3] max-w-md text-base md:text-lg leading-relaxed"
            >
              {section.body}
            </motion.p>
          )}

          {section.button_label && (
            <motion.button
              data-testid="hero-cta"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.25, duration: 0.8 }}
              onClick={followCta}
              className="group self-start flex items-center gap-4 font-oswald uppercase tracking-[0.2em] text-sm text-black bg-[#E85A24] px-8 py-4 hover:bg-[#FF7038] transition-colors duration-300"
            >
              {section.button_label}
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </motion.button>
          )}
        </div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 right-6 md:right-12 z-10 flex items-center gap-3 text-[#A3A3A3]"
      >
        <span className="font-oswald uppercase tracking-[0.3em] text-[10px]">
          {section.metadata?.scroll_label || "Scroll"}
        </span>
        <span className="h-10 w-px bg-white/20 relative overflow-hidden">
          <motion.span
            className="absolute top-0 left-0 h-3 w-px bg-[#E85A24]"
            animate={{ y: [0, 28, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          />
        </span>
      </motion.div>
    </section>
  );
};
